import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaUsers, 
  FaBook, 
  FaClipboardList, 
  FaExclamationTriangle,
  FaPlus,
  FaUserPlus
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config/api';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total_students: 0,
    total_homeworks: 0,
    submissions_by_status: {},
    upcoming_homeworks: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${config.apiURL}/teacher/dashboard`);
        setStats(response.data);
      } catch (err) {
        console.error('Erro ao carregar dados do dashboard:', err);
        setError('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <Icon className={`w-8 h-8 text-${color}-600`} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Bem-vindo, Prof. {user?.name}!
          </h1>
          <p className="mt-2 text-gray-600">
            Gerencie suas turmas e acompanhe o progresso dos alunos
          </p>
        </div>

        {/* Quick Actions */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/create-student"
            className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg flex items-center space-x-3 transition-colors"
          >
            <FaUserPlus className="w-6 h-6" />
            <span className="font-medium">Cadastrar Novo Aluno</span>
          </Link>
          
          <Link 
            to="/create-homework"
            className="bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg flex items-center space-x-3 transition-colors"
          >
            <FaPlus className="w-6 h-6" />
            <span className="font-medium">Criar Nova Lição</span>
          </Link>

          <Link 
            to="/students"
            className="bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-lg flex items-center space-x-3 transition-colors"
          >
            <FaUsers className="w-6 h-6" />
            <span className="font-medium">Ver Todos os Alunos</span>
          </Link>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            title="Total de Alunos" 
            value={stats.total_students} 
            icon={FaUsers}
            color="blue"
          />
          <StatCard 
            title="Total de Lições" 
            value={stats.total_homeworks} 
            icon={FaBook}
            color="green"
          />
          <StatCard 
            title="Aguardando Correção" 
            value={stats.submissions_by_status['Concluído'] || 0} 
            icon={FaClipboardList}
            color="yellow"
          />
          <StatCard 
            title="Lições Atrasadas" 
            value={stats.submissions_by_status['Atrasado'] || 0} 
            icon={FaExclamationTriangle}
            color="red"
          />
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Status das Lições
            </h2>
            <div className="space-y-3">
              {Object.entries(stats.submissions_by_status).map(([status, count]) => (
                <div key={status} className="flex justify-between items-center">
                  <span className="text-gray-600">{status}</span>
                  <span className="font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Próximas Lições
            </h2>
            {stats.upcoming_homeworks.length > 0 ? (
              <div className="space-y-3">
                {stats.upcoming_homeworks.map((homework) => (
                  <div key={homework.id} className="border-l-4 border-blue-500 pl-4 py-2">
                    <p className="font-medium text-gray-900">{homework.title}</p>
                    <p className="text-sm text-gray-600">
                      Vencimento: {new Date(homework.due_date).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaBook className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhuma lição próxima do vencimento</p>
                <p className="text-sm mt-2">
                  Comece criando uma nova lição para seus alunos
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Getting Started */}
        {stats.total_students === 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Primeiros Passos
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="bg-blue-100 text-blue-800 rounded-full p-2">
                  <span className="text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Cadastre seus alunos</h3>
                  <p className="text-gray-600">
                    Adicione alunos e seus responsáveis para começar a usar o sistema
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-green-100 text-green-800 rounded-full p-2">
                  <span className="text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Crie suas primeiras lições</h3>
                  <p className="text-gray-600">
                    Faça upload de arquivos e defina prazos para suas lições
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-purple-100 text-purple-800 rounded-full p-2">
                  <span className="text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Acompanhe o progresso</h3>
                  <p className="text-gray-600">
                    Corrija lições e forneça feedback aos alunos
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard; 